import React, { PropsWithChildren, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  MediaCenterProvider,
  ResourceFields,
} from '@brightmls/nextgen.lae.mediacenter';
import { ListingResource, useListingField } from '@brightmls/nextgen.lae.flc';
import { MediaCenterDataLoader } from 'src/pages/edit/media/MediaCenterDataLoader';
import { useMediaCenterConfig } from 'src/pages/edit/media/context/useMediaCenterConfig';
import { useMediaHandlers } from 'src/pages/edit/media/context/useMediaHandlers';
import { useWebSocketContext } from 'src/contexts/WebSocketContext';
import { useMediaLogger } from 'src/pages/edit/media/context/useMediaLogger';
import { PropertyField } from 'src/system/field';

interface ConfigLoaderProps {
  config: any;
  handlers: any;
}

const ConfigLoader = ({
  config,
  handlers,
  children,
}: PropsWithChildren<ConfigLoaderProps>) => {
  if (!config) return <>Loading component...</>;
  return (
    <MediaCenterProvider config={config} {...handlers}>
      <MediaCenterDataLoader>{children}</MediaCenterDataLoader>
    </MediaCenterProvider>
  );
};

export const OfficeExclusiveWrapper = ({ children }: PropsWithChildren) => {
  const { listingKey } = useParams<{ listingKey: string }>();
  const { connectionId } = useWebSocketContext();

  const logger = useMediaLogger('OfficeExclusiveComponent');
  // Create a field cache Map that will be populated with FLC field states
  const fieldCache = useMemo(() => new Map<string, any>(), []);

  const listingIdField = useListingField(
    ListingResource.Property,
    PropertyField.LISTING_ID,
    '',
  );
  const listingId = listingIdField?.value as string | undefined;

  const resource: ResourceFields = useMemo(
    () => ({
      get: (fieldName: string) => {
        // Check if field is in cache
        if (fieldCache.has(fieldName)) {
          return fieldCache.get(fieldName);
        }

        // Return a basic field state if not cached
        return {
          value: undefined,
          isDirty: false,
          isValid: true,
          errors: [],
          warnings: [],
          isHidden: false,
          isRequired: false,
          addToList: [],
          removeFromList: [],
          renderCount: 0,
          hasErrors: false,
          hasWarnings: false,
          isDisabled: false,
          updateValue: (value: any) => {
            logger.log(`Update field ${fieldName}:`, value);
          },
        };
      },
      set: (fieldName: string, value: any) => {
        const field = fieldCache.get(fieldName);
        if (field?.updateValue) {
          field.updateValue(value);
        } else {
          logger.log(`Set field ${fieldName}:`, value);
        }
      },
    }),
    [fieldCache, logger],
  );

  const config = useMediaCenterConfig(listingKey!, resource);
  const handlers = useMediaHandlers(listingKey!, { connectionId, listingId });

  return (
    <ConfigLoader config={config} handlers={handlers}>
      {children}
    </ConfigLoader>
  );
};

export default OfficeExclusiveWrapper;
